import { useMutation }                                              from '@apollo/client'
import { ArrowUturnLeftIcon }                                       from '@heroicons/react/24/solid'
import { useState }                                                 from 'react'
import { useDispatch }                                              from 'react-redux'
import { useNavigate }                                              from 'react-router-dom'
import { setDeliveryOptionsRefetch }                                from '../../../features/shop/shopSlice'
import { setToasts, showToaster }                                   from '../../../features/toaster/toasterSlice'
import { CREATE_SHOP_DELIVERY_OPTION, UPDATE_SHOP_DELIVERY_OPTION } from '../../../graphql/shop'
import { Button, TextField }                                        from '../../index'
import { LoadingEllipsis }                                          from '../../Loaders'

export default function SettingsShopDeliveryOptionForm( props: any ): JSX.Element {
  const { deliveryOption }                                                = props
  const dispatch: any                                                     = useDispatch()
  const navigate: any                                                     = useNavigate()
  const [ deliveryOptionName, setDeliveryOptionName ]                     = useState( deliveryOption?.name ?? '' )
  const [ deliveryOptionAmount, setDeliveryOptionAmount ]                 = useState( deliveryOption?.amount ? deliveryOption.amount.toFixed( 2 ) : '0' )
  const [ deliveryOptionConfirmation, setDeliveryOptionConfirmation ]     = useState( deliveryOption?.confirmation_email ?? '' )
  const [ deliveryOptionConfirmationCC, setDeliveryOptionConfirmationCC ] = useState( deliveryOption?.confirmation_email_cc ?? '' )
  const [ nameError, setNameError ]                                       = useState( false )
  const [ amountError, setAmountError ]                                   = useState( false )
  const [ confirmationError, setConfirmationError ]                       = useState( false )
  const [ confirmationCCError, setConfirmationCCError ]                   = useState( false )

  const resetErrors: any = (): void => {
    setNameError( false )
    setAmountError( false )
    setConfirmationError( false )
    setConfirmationCCError( false )
  }

  const checkErrors: any = (): boolean => {
    resetErrors()

    let hasErrors: boolean = false

    if ( !deliveryOptionName.length || deliveryOptionName.length < 3 ) {
      setNameError( true )
      hasErrors = true
    }

    if ( !deliveryOptionAmount.length || isNaN( parseFloat( deliveryOptionAmount ) ) ) {
      setAmountError( true )
      hasErrors = true
    }

    if ( !deliveryOptionConfirmation.length || deliveryOptionConfirmation.length < 6 ) {
      setConfirmationError( true )
      hasErrors = true
    }

    return hasErrors
  }

  const handleSubmit: any = async (): Promise<any> => {
    if ( checkErrors() || creating || updating ) return

    if ( deliveryOption?.name === deliveryOptionName &&
         deliveryOption?.amount === deliveryOptionAmount &&
         deliveryOption?.confirmation_email === deliveryOptionConfirmation &&
         deliveryOption?.confirmation_email_cc === deliveryOptionConfirmationCC
    ) return

    const formData: any = {
      name:                  deliveryOptionName,
      amount:                deliveryOptionAmount,
      confirmation_email:    deliveryOptionConfirmation,
      confirmation_email_cc: deliveryOptionConfirmationCC
    }

    if ( deliveryOption ) {
      formData.id = deliveryOption.id
      await updateDeliveryOption( { variables: { input: formData } } )
    } else {
      await createDeliveryOption( { variables: { input: formData } } )
    }

    await dispatch( setDeliveryOptionsRefetch( true ) )
  }

  const [ createDeliveryOption, { loading: creating } ] = useMutation( CREATE_SHOP_DELIVERY_OPTION, {
    onCompleted: (): void => {
      navigate( '/settings/shop/delivery-options' )
      dispatchMessage( 'success', 'Delivery option created!' )
    },
    onError:     (): void => dispatchMessage( 'error', 'Creating failed. Please try again' )
  } )

  const [ updateDeliveryOption, { loading: updating } ] = useMutation( UPDATE_SHOP_DELIVERY_OPTION, {
    onCompleted: (): void => dispatchMessage( 'success', 'Delivery option updated!' ),
    onError:     (): void => dispatchMessage( 'error', 'Update failed. Please try again' )
  } )

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  return (
    <>
      <div className="w-24 my-4">
        <Button
          label="Back"
          blue
          onClick={ (): any => navigate( -1 ) }
          icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
        />
      </div>
      <div className="flex flex-col w-full bg-white rounded-3xl p-4 xl:p-5 space-y-3">
        { ( creating || updating ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

        { !creating && !updating &&
          <>
            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="text-blue-header w-full mb-3">Name</div>
              <TextField
                label=""
                value={ deliveryOptionName }
                name="name"
                type="text"
                onChange={ setDeliveryOptionName }
                placeholder="Name"
                wrapperClasses="w-full"
                fieldClasses={ `py-4 px-5 border ${ nameError ? 'border-red-600' : 'border-grey-light' } rounded-2xl w-full text-blue-input font-medium placeholder-grey-input` }
                isRequired
              />
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="text-blue-header w-full mb-3">Amount</div>
              <TextField
                label=""
                value={ deliveryOptionAmount }
                name="amount"
                type="number"
                onChange={ setDeliveryOptionAmount }
                step={ 0.01 }
                placeholder="Amount"
                wrapperClasses="w-full relative"
                fieldClasses={ `py-4 pl-7 pr-5 border ${ amountError ? 'border-red-600' : 'border-grey-light' } rounded-2xl w-full text-blue-input font-medium placeholder-grey-input` }
                poundMark
                isRequired
              />
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="text-blue-header w-full mb-3">Confirmation Email</div>
              <TextField
                label=""
                value={ deliveryOptionConfirmation }
                name="confirmationEmail"
                type="text"
                onChange={ setDeliveryOptionConfirmation }
                placeholder="Confirmation Email"
                wrapperClasses="w-full"
                fieldClasses={ `py-4 px-5 border ${ confirmationError ? 'border-red-600' : 'border-grey-light' } rounded-2xl w-full text-blue-input font-medium placeholder-grey-input` }
                isRequired
              />
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="text-blue-header w-full mb-3">Confirmation Emails CC</div>
              <TextField
                label=""
                value={ deliveryOptionConfirmationCC }
                name="confirmationEmailCC"
                type="text"
                onChange={ setDeliveryOptionConfirmationCC }
                placeholder="Confirmation Email CC"
                wrapperClasses="w-full"
                fieldClasses={ `py-4 px-5 border ${ confirmationCCError ? 'border-red-600' : 'border-grey-light' } rounded-2xl w-full text-blue-input font-medium placeholder-grey-input` }
                isRequired
              />
            </div>

            <div className="flex flex-wrap w-full">
              <div className="flex pt-5">
                <Button label={ deliveryOption ? 'Update delivery' : 'Add new delivery' } yellow onClick={ handleSubmit } />
              </div>
            </div>
          </>
        }
      </div>
    </>
  )
}
